// ResumenRecepcionesListado.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { RefreshCw, X, Search, FileSpreadsheet } from "lucide-react";
import dayjs from "dayjs";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { DataTable } from "./components/ui/data-table";
import { DateRangePicker } from "./components/ui/date-range-picker";
import { toast } from "./lib/toast";
import apiUrl from "./config/config";

const token =
  "Bearer 0j2whpl3*s+2/j-hCl!!-5A-8Corubp5[gqzhGrA.b*hBdO[+r.yGIFpvpOqv";

const parseResponse = (data) => {
  try {
    if (data && typeof data === "object") {
      if ("d" in data) return typeof data.d === "string" ? JSON.parse(data.d) : data.d;
      return data;
    }
    return JSON.parse(String(data ?? ""));
  } catch { return null; }
};

const trimStr = (v) => (typeof v === "string" ? v.trim() : v);

const ResumenRecepcionesListado = () => {
  const [loading, setLoading] = useState(false);
  const [rowsRaw, setRowsRaw] = useState([]);
  const [range, setRange] = useState([dayjs(), dayjs()]);
  const [searchText, setSearchText] = useState("");
  const [searchTextDebounced, setSearchTextDebounced] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  useEffect(() => {
    const t = setTimeout(() => setSearchTextDebounced(searchText.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [searchText]);

  const normalize = (arr) =>
    (Array.isArray(arr) ? arr : []).map((r, i) => {
      const fecha = r.FechaRecepcion ? dayjs(r.FechaRecepcion).format("DD-MM-YYYY") : "";
      const productor = trimStr(r.Productor);
      const especie = trimStr(r.Especie);
      const variedad = trimStr(r.DescripcionVariedad);
      const guia = trimStr(r.GuiaEntrega);
      const empresa = trimStr(r.EmpresaExportadora);
      const centro = trimStr(r.DescripCentroAcopio);
      const haystack = [fecha, productor, especie, variedad, guia, empresa, centro, r.NumeroRecepcion, r.IdRecepcion, r.ProductorReal, r.GuiaEntregaReal, r.DescripcionEnvase].join(" ").toLowerCase();
      return { key: r.IdRecepcion ?? i, fecha, productor, especie, variedad, nombre: trimStr(r.Nombre), numeroRecepcion: r.NumeroRecepcion, idRecepcion: r.IdRecepcion, guiaEntrega: guia, centroAcopio: centro, envase: trimStr(r.DescripcionEnvase), cantidad: r.Cantidad, kilosAprobados: r.KilosAprobados, kilosBrutos: r.KilosBrutos, idEspecie: r.IdEspecie, empresaExportadora: empresa, productorReal: r.ProductorReal, guiaEntregaReal: r.GuiaEntregaReal, _haystack: haystack };
    });

  const fetchResumen = useCallback(async () => {
    try {
      if (!range || range.length !== 2) { toast.error("Selecciona un rango de fechas."); return; }
      const fechaInicio = dayjs(range[0]).format("YYYY-MM-DD");
      const fechaFin = dayjs(range[1]).format("YYYY-MM-DD");
      setLoading(true);
      const res = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetResumenRecepcion`, { headers: { Authorization: token }, params: { fechaInicio, fechaFin } });
      console.log(res.data);
      const payload = parseResponse(res.data) ?? (res.data?.d ? JSON.parse(res.data.d) : res.data);
      if (payload && !Array.isArray(payload) && payload.error) { toast.error(String(payload.error)); setRowsRaw([]); return; }
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      setRowsRaw(normalize(list));
      setPagination((p) => ({ ...p, current: 1 }));
    } catch (err) {
      console.error(err);
      toast.error("Error al consultar el resumen.");
    } finally {
      setLoading(false);
    }
  }, [range]);

  const filteredRows = useMemo(() => {
    if (!searchTextDebounced) return rowsRaw;
    return rowsRaw.filter((r) => r._haystack?.includes(searchTextDebounced));
  }, [rowsRaw, searchTextDebounced]);

  const columns = useMemo(() => [
    { title: "Fecha", dataIndex: "fecha", width: 120 },
    { title: "Productor", dataIndex: "productor", ellipsis: true, width: 220 },
    { title: "Especie", dataIndex: "especie", width: 120 },
    { title: "Variedad", dataIndex: "variedad", ellipsis: true, width: 160 },
    { title: "N° Recepción", dataIndex: "numeroRecepcion", width: 120 },
    { title: "ID Recepción", dataIndex: "idRecepcion", width: 120 },
    { title: "Guía Entrega", dataIndex: "guiaEntrega", width: 130 },
    { title: "Envase", dataIndex: "envase", ellipsis: true, width: 140 },
    { title: "Cantidad", dataIndex: "cantidad", align: "right", width: 110 },
    { title: "K. Aprobados", dataIndex: "kilosAprobados", align: "right", width: 120 },
    { title: "K. Brutos", dataIndex: "kilosBrutos", align: "right", width: 110 },
    { title: "Empresa Exp.", dataIndex: "empresaExportadora", ellipsis: true, width: 200 },
  ], []);

  const handleExportExcel = useCallback(async () => {
    try {
      const XLSX = await import("xlsx");
      const data = filteredRows.map((r) => ({ "Fecha": r.fecha, "Productor": r.productor, "Especie": r.especie, "Variedad": r.variedad, "N° Recepción": r.numeroRecepcion, "ID Recepción": r.idRecepcion, "Guía Entrega": r.guiaEntrega, "Centro Acopio": r.centroAcopio, "Envase": r.envase, "Cantidad": r.cantidad, "Kilos Aprobados": r.kilosAprobados, "Kilos Brutos": r.kilosBrutos, "Empresa Exportadora": r.empresaExportadora }));
      const ws = XLSX.utils.json_to_sheet(data);
      const headers = Object.keys(data[0] || {});
      ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length, ...data.map((row) => (row[h] ? String(row[h]).length : 0))) + 2 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Resumen");
      XLSX.writeFile(wb, `ResumenRecepciones_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
    } catch (e) { console.error(e); toast.error("No se pudo exportar a Excel."); }
  }, [filteredRows]);

  const handleLimpiar = () => {
    setRange([dayjs(), dayjs()]);
    setSearchText("");
    setRowsRaw([]);
    setPagination({ current: 1, pageSize: 20 });
  };

  useEffect(() => { fetchResumen(); }, []); // eslint-disable-line

  return (
    <div className="bg-white p-4 rounded-xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 m-0">Resumen de Recepciones</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Buscar productor / especie / variedad / guía / empresa / centro"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 w-80"
            />
            {searchText && (
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearchText("")}>
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
          <DateRangePicker
            value={range}
            onChange={(vals) => setRange(vals || [null, null])}
            format="YYYY-MM-DD"
            allowClear={false}
          />
          <Button onClick={fetchResumen} loading={loading}>
            <RefreshCw className="h-4 w-4" /> Buscar
          </Button>
          <Button variant="outline" onClick={handleLimpiar}>
            <X className="h-4 w-4" /> Limpiar
          </Button>
          <Button variant="outline" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
          </Button>
        </div>
      </div>

      <DataTable
        loading={loading}
        columns={columns}
        data={filteredRows}
        rowKey="key"
        bordered
        scroll={{ x: 1400 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100, 200],
          showTotal: (total, [start, end]) => `${start}-${end} de ${total} registros`,
        }}
        onPaginationChange={(page, pageSize) => setPagination({ current: page, pageSize })}
      />
    </div>
  );
};

export default ResumenRecepcionesListado;
